/**
 * Email Compose Controller — signal-store-backed compose form.
 *
 * Manages:
 *   - Draft autosave (via EmailAutosave module + store.draftId signal)
 *   - CC/BCC row toggle
 *   - Mailbox change → signature swap
 *   - File attachment badge management
 *   - Send (sync editor → stop autosave → requestSubmit)
 *   - Discard (confirm → stop autosave → DELETE draft → clear reading pane)
 *   - composeDirty signal sync
 *
 * The editor is a plain contenteditable div (not TinyMCE).
 * TinyMCE is only used for the quick reply form (email_reply_form_controller.js).
 */

import { Controller } from '@hotwired/stimulus'
import { destroy } from '@rails/request.js'
import { I18n } from 'i18n-js'
import locales from '../locales.json'
import { EmailAutosave } from '../modules/email/email-autosave.js'

export default class extends Controller {
  static targets = [
    'form', 'toField', 'ccField', 'bccField', 'ccRow', 'bccRow',
    'subject', 'body', 'editor', 'mailboxSelect',
    'fileInput', 'fileList', 'signature', 'draftIndicator', 'sendBtn'
  ]
  static values = {
    draftId:          Number,
    mailboxId:        Number,
    composeType:      String,
    autosaveInterval: { type: Number, default: 5000 }
  }

  connect() {
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content || 'hu'

    const store = this._store()

    // Restore draftId from store if already set (e.g. editing existing draft)
    if (store && store.draftId.value) {
      this.draftIdValue = store.draftId.value
    }

    // Build autosave instance
    this._autosave = new EmailAutosave({
      store,
      getPayload: () => this._buildPayload(),
      onSaved: () => this._showDraftSaved(),
      onCreated: (id) => {
        this.draftIdValue = id
      },
      intervalMs: this.autosaveIntervalValue
    })
    this._autosave.start()
  }

  disconnect() {
    this._autosave?.stop()
    const store = this._store()
    if (store) store.closeCompose()
  }

  // ── CC/BCC toggle ─────────────────────────────────────────────────────────

  toggleCcBcc() {
    const ccRow  = this.hasCcRowTarget  ? this.ccRowTarget  : this.element.querySelector("[data-email-compose-target='ccRow']")
    const bccRow = this.hasBccRowTarget ? this.bccRowTarget : this.element.querySelector("[data-email-compose-target='bccRow']")
    if (ccRow)  ccRow.classList.toggle('d-none')
    if (bccRow) bccRow.classList.toggle('d-none')
  }

  // ── Mailbox change → signature swap ──────────────────────────────────────

  async changeMailbox(e) {
    const mailboxId = e.target.value
    this.mailboxIdValue = mailboxId
    if (!mailboxId) return

    try {
      const res  = await fetch(`/mailboxes/${mailboxId}/signature/html`, {
        headers: { Accept: 'application/json' }
      })
      const data = await res.json()
      const sigHtml = data.signature || ''
      const editor  = this.hasEditorTarget ? this.editorTarget : null
      if (!editor) return

      const existing = editor.querySelector('.email-signature')
      if (sigHtml) {
        const tmp = document.createElement('div')
        tmp.innerHTML = sigHtml
        const sigEl = tmp.firstElementChild
        if (sigEl) {
          if (existing) {
            existing.replaceWith(sigEl)
          } else {
            const quote = editor.querySelector('.email-quoted-content')
            if (quote) editor.insertBefore(sigEl, quote)
            else       editor.appendChild(sigEl)
          }
        }
      } else if (existing) {
        existing.remove()
      }
    } catch (_) { /* silent */ }
  }

  // ── File attachments ──────────────────────────────────────────────────────

  attachFiles(e) {
    const input = e.currentTarget
    if (!input.files.length) return

    // Clone the input so re-selection of the same file works
    const clone  = input.cloneNode(true)
    clone.value  = ''
    input.after(clone)

    const listEl = this.hasFileListTarget ? this.fileListTarget : null
    Array.from(input.files).forEach(file => {
      const badge = document.createElement('span')
      badge.className = 'badge bg-secondary-subtle text-dark border d-flex align-items-center gap-1 p-1'
      badge.innerHTML = `<i class="bi bi-paperclip small"></i>${this._escapeHtml(file.name)}`

      const removeBtn = document.createElement('button')
      removeBtn.type      = 'button'
      removeBtn.className = 'btn-close btn-close-dark ms-1'
      removeBtn.style.fontSize = '8px'
      removeBtn.addEventListener('click', () => {
        badge.remove()
        input.value = ''
      })
      badge.appendChild(removeBtn)

      if (listEl) listEl.appendChild(badge)
      else        input.before(badge)
    })
  }

  // ── Manual save ───────────────────────────────────────────────────────────

  async saveDraft() {
    await this._autosave.saveNow()
  }

  // ── Send ──────────────────────────────────────────────────────────────────

  send(e) {
    e.preventDefault()
    this.syncEditorToBody()
    this._autosave.stop()

    if (this.hasFormTarget) {
      this.formTarget.requestSubmit()
    } else {
      this.element.querySelector('form')?.requestSubmit()
    }
  }

  // ── Discard ───────────────────────────────────────────────────────────────

  discard(e) {
    e?.preventDefault?.()
    if (!confirm(this.i18n.t('emails.discard_confirm'))) return

    this._autosave.stop()

    const draftId = this.draftIdValue || this._store()?.draftId.value
    if (draftId) {
      destroy(`/emails/drafts/${draftId}`, { responseKind: 'turbo-stream' })
    }

    const store = this._store()
    if (store) store.closeCompose()

    const readingPane = document.getElementById('email_reading_pane')
    if (readingPane) {
      readingPane.innerHTML = ''
      readingPane.removeAttribute('src')
    }
  }

  // ── Editor sync ───────────────────────────────────────────────────────────

  syncEditorToBody() {
    if (this.hasEditorTarget && this.hasBodyTarget) {
      this.bodyTarget.value = this.editorTarget.innerHTML
      const store = this._store()
      if (store) store.markComposeDirty()
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  _buildPayload() {
    this.syncEditorToBody()
    if (!this.hasBodyTarget) return null
    const body = this.bodyTarget.value
    if (!body) return null

    return {
      email_draft: {
        body,
        subject:    this.hasSubjectTarget    ? this.subjectTarget.value    : '',
        mailbox_id: this.hasMailboxSelectTarget ? this.mailboxSelectTarget.value : this.mailboxIdValue,
        to_addresses: this.hasToFieldTarget  ? this._getAddresses(this.toFieldTarget) : [],
      }
    }
  }

  _showDraftSaved() {
    if (!this.hasDraftIndicatorTarget) return
    this.draftIndicatorTarget.textContent = this.i18n.t('emails.draft_saved')
    setTimeout(() => {
      if (this.hasDraftIndicatorTarget) this.draftIndicatorTarget.textContent = ''
    }, 2000)
  }

  _getAddresses(target) {
    if (!target) return []
    return target.value.split(/[,;]/).map(s => s.trim()).filter(Boolean)
  }

  _escapeHtml(str) {
    return str.replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    )
  }

  _store() {
    return window._emailStore || null
  }
}
